<script setup>
import { ref, onMounted, computed, watch } from 'vue'

definePageMeta({
  layout: 'dashboard',
  middleware: 'auth',
  ssr: false,
})

useHead({
  title: 'Inställningar',
})

const router = useRouter()
const { $bgaApi } = useNuxtApp()

// State variables
const integrationSettings = ref([])
const searchQuery = ref('')
const currentPage = ref(1)
const itemsPerPage = 50

// Fetch integration settings
const fetchIntegrationSettings = async () => {
  try {
    const response = await $bgaApi.get('bga/integration-settings')
    const data = response.data

    // Combine adyen_settings and klarna_settings into one array with integration type
    integrationSettings.value = [
      ...data.adyen_settings.map((item) => ({ ...item, type: 'Adyen' })),
      ...data.klarna_settings.map((item) => ({ ...item, type: 'Klarna' })),
    ]
  } catch (error) {
    // console.error("Error fetching integration settings:", error);
  }
}

// Computed property for filtered data based on search query
const filteredData = computed(() => {
  return integrationSettings.value.filter((item) => {
    const query = searchQuery.value.toLowerCase()
    return (
      item.type.toLowerCase().includes(query) ||
      item.merchant_id?.toLowerCase().includes(query) ||
      (item.active ? 'active' : 'inactive').toLowerCase().includes(query)
    )
  })
})

const totalItems = computed(() => filteredData.value.length)

// Computed property for paginated data
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filteredData.value.slice(start, end)
})

// Change page function
const changePage = (page) => {
  if (page > 0 && page <= Math.max(1, Math.ceil(totalItems.value / itemsPerPage))) {
    currentPage.value = page
  }
}

// Navigate to integration settings detail
const goToIntegrationSettings = (item) => {
  if (item.type === 'Adyen') {
    router.push(`/integration-settings/adyen/${item.id}`)
  } else if (item.type === 'Klarna') {
    router.push(`/integration-settings/klarna/${item.id}`)
  }
}

// Watch for changes in searchQuery and reset to the first page
watch(searchQuery, () => {
  currentPage.value = 1
})

// Run the API call when the component mounts
onMounted(() => {
  fetchIntegrationSettings()
})
</script>

<template>
  <div class="dashboard-stack">
    <div class="dashboard-header">
      <div class="dashboard-header__titles">
        <h1 class="dashboard-page__title">Inställningar</h1>
        <p class="dashboard-page__subtitle lp-body">
          Hantera dina integrationsinställningar för Klarna och Adyen.
        </p>
      </div>
    </div>

    <AppListHeader
      :search="searchQuery"
      :items-per-page="''"
      search-placeholder="Sök integration eller handlar-ID"
      @update:search="(value) => (searchQuery = value)"
    />

    <AppCard class="app-card--table">
      <div class="app-table-wrap">
        <table class="app-table">
          <thead>
            <tr>
              <th>Integrationstyp</th>
              <th>Handlar-ID</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in paginatedData"
              :key="item.id"
              class="app-table__row--clickable"
              @click="goToIntegrationSettings(item)"
            >
              <td class="app-table__cell-strong">{{ item.type }}</td>
              <td class="app-table__cell-strong">{{ item.merchant_id || '' }}</td>
              <td>
                <AppChip :variant="item.active ? 'success' : 'secondary'">
                  {{ item.active ? 'Aktiv' : 'Inaktiv' }}
                </AppChip>
              </td>
            </tr>
            <tr v-if="!paginatedData.length">
              <td class="app-table__empty" colspan="3">Inga integrationer hittades.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <AppPagination
        :page="currentPage"
        :items-per-page="itemsPerPage"
        :total-items="totalItems"
        @update:page="changePage"
      />
    </AppCard>
  </div>
</template>
